// AGC: Modern wallet provider using wagmi, RainbowKit, and Farcaster SDK
// AGC: Supports Coinbase Smart Wallet, MetaMask, and WalletConnect
// AGC: Optimized for Base blockchain with Base App integration
// AGC: Shared across all FarmProHub apps

import React, { ReactNode, useEffect, useMemo, useRef, lazy, Suspense } from 'react'
import { WagmiProvider as PrivyWagmiProvider } from '@privy-io/wagmi'
import { WagmiProvider as CoreWagmiProvider, useAccount, useSwitchChain } from 'wagmi'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RainbowKitProvider, darkTheme } from '@rainbow-me/rainbowkit'
import { OnchainKitProvider } from '@coinbase/onchainkit'
import { config, rainbowKitTheme } from '../config/wagmi-unified'
import { useFeatureFlags } from '../hooks/useFeatureFlags'
import { baseSepolia } from '../config/networks'
import { getAppMetadata } from '../config/app-metadata'
import { usePrivy } from '@privy-io/react-auth'

const LazyBaseAppStack = lazy(() => import('./BaseAppStack'))

import '@rainbow-me/rainbowkit/styles.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 3,
      refetchOnWindowFocus: true,
      onError: (error: any) => {
        if (error?.message?.includes('/dev/null') || error?.message?.includes('not found')) {
          return
        }
        console.error('Query error:', error)
      },
    },
  },
})

export interface WalletProviderProps {
  children: ReactNode
}

const PRIVY_APP_ID = import.meta.env.VITE_PRIVY_APP_ID
const PRIVY_ENABLED = import.meta.env.VITE_ENABLE_PRIVY === 'true' && Boolean(PRIVY_APP_ID)

const PrivySmartWalletBootstrapInner: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { ready, authenticated, login, logout } = usePrivy()
  const { isBaseAppContext } = useFeatureFlags()
  const hasAttemptedLoginRef = useRef(false)

  useEffect(() => {
    if (!PRIVY_ENABLED || isBaseAppContext || typeof window === 'undefined') {
      return
    }

    const privyBridge = { ready, authenticated, login, logout }
    ;(window as typeof window & { privy?: typeof privyBridge }).privy = privyBridge

    return () => {
      if ((window as typeof window & { privy?: typeof privyBridge }).privy === privyBridge) {
        delete (window as typeof window & { privy?: typeof privyBridge }).privy
      }
    }
  }, [authenticated, isBaseAppContext, login, logout, ready])

  useEffect(() => {
    if (isBaseAppContext) {
      console.log('🚫 AGC: Skipping Privy auto-login in Base App context')
      return
    }

    if (!ready || authenticated || hasAttemptedLoginRef.current) {
      return
    }

    hasAttemptedLoginRef.current = true
    console.log('🔐 AGC: Auto-triggering Privy login to provision embedded smart wallet')
    login()
      .then(() => {
        console.log('✅ AGC: Privy smart wallet session established')
      })
      .catch((error: unknown) => {
        console.error('❌ AGC: Privy auto-login failed:', error)
        setTimeout(() => {
          hasAttemptedLoginRef.current = false
        }, 3000)
      })
  }, [authenticated, isBaseAppContext, login, ready])

  return <>{children}</>
}

const PrivySmartWalletBootstrap: React.FC<{ children: ReactNode }> = ({ children }) => {
  if (!PRIVY_ENABLED) {
    return <>{children}</>
  }

  return <PrivySmartWalletBootstrapInner>{children}</PrivySmartWalletBootstrapInner>
}

const ChainEnforcer: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { chain, isConnected } = useAccount()
  const { switchChain } = useSwitchChain()
  const { isBaseAppContext } = useFeatureFlags()

  useEffect(() => {
    if (!isBaseAppContext || !isConnected || !chain) return
    if (chain.id !== baseSepolia.id) {
      console.log(`⚠️ Detected ${chain.name} (${chain.id}) - forcing Base Sepolia`)
      switchChain?.({ chainId: baseSepolia.id })
    }
  }, [chain, isConnected, isBaseAppContext, switchChain])

  useEffect(() => {
    if (!isBaseAppContext) return
    if (chain?.id === baseSepolia.id) {
      localStorage.setItem('farmprohub_preferred_chain', baseSepolia.id.toString())
    }

    const preferredChain = localStorage.getItem('farmprohub_preferred_chain')
    if (preferredChain === baseSepolia.id.toString() && chain?.id !== baseSepolia.id) {
      console.log('🔄 Restoring Base Sepolia from localStorage')
      switchChain?.({ chainId: baseSepolia.id })
    }
  }, [chain, isBaseAppContext, switchChain])

  return <>{children}</>
}

export const WalletProvider: React.FC<WalletProviderProps> = ({ children }) => {
  const { isBaseAppContext } = useFeatureFlags()
  const metadata = getAppMetadata()

  if (isBaseAppContext) {
    console.log('🎯 AGC: Base App context detected - enabling Farcaster provider stack')
    return (
      <Suspense fallback={<div className="text-white p-6 text-center">Loading Base App…</div>}>
        <LazyBaseAppStack>{children}</LazyBaseAppStack>
      </Suspense>
    )
  }

  const shouldUsePrivy = PRIVY_ENABLED
  const SelectedWagmiProvider = useMemo(
    () => (shouldUsePrivy ? PrivyWagmiProvider : CoreWagmiProvider),
    [shouldUsePrivy],
  )

  const wrappedChildren = shouldUsePrivy ? (
    <PrivySmartWalletBootstrap>{children}</PrivySmartWalletBootstrap>
  ) : (
    children
  )

  const ONCHAINKIT_API_KEY = import.meta.env.VITE_ONCHAINKIT_API_KEY || ''

  return (
    <QueryClientProvider client={queryClient}>
      <SelectedWagmiProvider config={config} reconnectOnMount>
        <RainbowKitProvider
          theme={darkTheme({
            ...rainbowKitTheme,
            colors: {
              accentColor: rainbowKitTheme.accentColor,
              accentColorForeground: rainbowKitTheme.accentColorForeground,
            },
          })}
          modalSize="compact"
          showRecentTransactions
          appInfo={{
            appName: metadata.name,
            learnMoreUrl: metadata.url,
          }}
        >
          <OnchainKitProvider
            apiKey={ONCHAINKIT_API_KEY}
            chain={baseSepolia}
            config={{
              appearance: {
                name: metadata.name,
                logo: '/logo.png',
                mode: 'auto',
              },
              wallet: {
                display: 'modal',
              },
            }}
            miniKit={{
              enabled: false,
            }}
          >
            <ChainEnforcer>{wrappedChildren}</ChainEnforcer>
          </OnchainKitProvider>
        </RainbowKitProvider>
      </SelectedWagmiProvider>
    </QueryClientProvider>
  )
}

